<!--
  Account.svelte — User profile settings page.

  Loads the authenticated user's full profile on mount and presents
  an editable form covering:
    - Avatar upload with crop-to-square
    - Personal information (name, username, about)
    - Contact details (company, website, location)
    - Password change
    - Preferences (theme, language, newsletter)

  All changes are saved via the AppNetwork profile endpoints.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Input,
		Label,
		Heading,
		Textarea,
		Spinner,
		Helper,
		Avatar,
		Toggle,
		Modal
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import { currentUser, loadFullProfile, colorTheme } from '../../Auth';
	import {
		updateProfile,
		changePassword,
		uploadAvatar,
		deleteAvatar,
		setupTwoFactor,
		confirmTwoFactor,
		disableTwoFactor,
		refreshToken,
		requestEmailChangeOTP,
		confirmEmailChange,
		requestUsernameChangeOTP,
		confirmUsernameChange,
		checkUsernameAvailability,
		requestAccountDeletionOTP,
		confirmAccountDeletion
	} from '../../AppNetwork';
	import { setToken, logout } from '../../Auth';
	import type { TwoFactorSetupResponse } from '../../AppTypes';
	import QRCode from 'qrcode';
	import site from '../../site';
	import type { User } from '../../AppTypes';
	import ImageCropper from '../../components/ImageCropper.svelte';
	import OTPInput from '../../components/OTPInput.svelte';
	import { ConfirmButton, RoutedLink } from '../../components';
	import { toastSuccess, toastWarning, toastError } from '../../toast';
	import { t, t2, locale, locales, localeNames } from '../../i18n';

	// ── State ─────────────────────────────────────────────────────

	/** Whether the initial profile load is in progress. */
	let loading = $state(true);
	/** Global error banner. */
	let error = $state('');

	/** Whether a profile save is in progress. */
	let savingProfile = $state(false);
	/** Whether a password change is in progress. */
	let savingPassword = $state(false);
	/** Feedback messages for individual sections. */
	let avatarError = $state('');

	// ── Form fields: Personal ─────────────────────────────────────

	let firstName = $state('');
	let lastName = $state('');
	let about = $state('');

	// ── Form fields: Contact ──────────────────────────────────────

	let company = $state('');
	let website = $state('');
	let location = $state('');

	// ── Form fields: Password ─────────────────────────────────────

	let currentPasswordField = $state('');
	let newPasswordField = $state('');
	let confirmPasswordField = $state('');

	// ── Form fields: Email Change ─────────────────────────────────

	/** New email the user wants to switch to. */
	let newEmailField = $state('');
	/** Whether the OTP has been requested (show OTP input). */
	let emailChangeOTPSent = $state(false);
	/** The 6-digit code the user enters. */
	let emailChangeCode = $state('');
	/** Whether a request/confirm is in-flight. */
	let emailChangeBusy = $state(false);

	// ── Form fields: Username Change ─────────────────────────────

	/** New username the user wants to switch to. */
	let newUsernameField = $state('');
	/** Whether the OTP has been requested (show OTP input). */
	let usernameChangeOTPSent = $state(false);
	/** The 6-digit code the user enters. */
	let usernameChangeCode = $state('');
	/** Whether a request/confirm is in-flight. */
	let usernameChangeBusy = $state(false);
	/** Live validation result: null = not checked, true = valid+available, false = invalid/taken. */
	let usernameCheckResult = $state<boolean | null>(null);
	/** Feedback message from the live check. */
	let usernameCheckMessage = $state('');
	/** Whether a username availability check is in-flight. */
	let usernameChecking = $state(false);
	/** Timer handle for debouncing the live check. */
	let usernameCheckTimer: ReturnType<typeof setTimeout> | undefined;

	// ── Account Deletion (GDPR self-service) ─────────────────────

	/** Whether the delete-account modal is open. */
	let deleteModalOpen = $state(false);
	/** Whether the deletion OTP has been requested (show OTP input). */
	let deletionOTPSent = $state(false);
	/** The 6-digit code the user enters to confirm deletion. */
	let deletionCode = $state('');
	/** Typed confirmation — must equal "DELETE" before the final step is enabled. */
	let deletionConfirmText = $state('');
	/** Whether a deletion request/confirm is in-flight. */
	let deletionBusy = $state(false);

	// ── Form fields: Preferences ──────────────────────────────────

	let themeMode = $state('dark');
	let languageSettingsMode = $state('en');
	/** Preferred sign-in method pre-selected on the login page. */
	let preferredAuthMethod = $state('email');
	let newsletterOptOut = $state(false);

	// ── 2FA State ────────────────────────────────────────────────

	/** Whether 2FA setup is in progress. */
	let twoFactorBusy = $state(false);
	/** 2FA setup response (secret + URI for QR code generation). */
	let twoFactorSetup: TwoFactorSetupResponse | null = $state(null);
	/** Data URL of the QR code generated from the otpauth URI. */
	let twoFactorQRDataURL = $state('');
	/** Backup codes returned after successful 2FA confirmation. */
	let backupCodes: string[] = $state([]);
	/** Feedback messages for 2FA section. */
	let twoFactorError = $state('');
	/** Whether to show the disable 2FA confirmation. */
	let showDisable2FA = $state(false);
	/** TOTP code for disabling 2FA. */
	let disable2FACode = $state('');

	// ── Avatar ────────────────────────────────────────────────────

	/** Whether the crop modal is open. */
	let showCropModal = $state(false);
	/** File selected for cropping. */
	let cropFile: File | null = $state(null);
	/** Whether an avatar upload/delete is in progress. */
	let avatarBusy = $state(false);
	/** Cache buster for the avatar img tag. */
	let avatarCacheBust = $state(Date.now());

	/**
	 * Build the avatar URL for the current user.
	 * Appends a cache-buster so the browser reloads after upload.
	 */
	let avatarUrl = $derived($currentUser?.imageName
		? `${site.backend.baseURL}/uploads/avatars/${$currentUser.imageName}?t=${avatarCacheBust}`
		: '');

	// ── Lifecycle ─────────────────────────────────────────────────

	onMount(async () => {
		try {
			const user = await loadFullProfile();
			if (user) populateForm(user);
			else error = t2('Failed to load profile.');
		} catch (err) {
			error = err instanceof Error ? err.message : t2('Failed to load profile');
		} finally {
			loading = false;
		}
	});

	/**
	 * Populate all form fields from a User record.
	 */
	function populateForm(u: User): void {
		firstName = u.firstName || '';
		lastName = u.lastName || '';
		about = u.about || '';
		company = u.company || '';
		website = u.website || '';
		location = u.location || '';
		themeMode = u.themeMode || 'dark';
		languageSettingsMode = u.languageSettingsMode || 'en';
		locale.set(languageSettingsMode);
		preferredAuthMethod = u.preferredAuthMethod || 'email';
		newsletterOptOut = u.newsletterOptOut;
	}

	// ── Handlers ──────────────────────────────────────────────────

	/**
	 * Save personal info + contact + preferences in one call.
	 */
	async function handleSaveProfile(): Promise<void> {
		savingProfile = true;

		/** Send the profile update and sync JWT + theme on success. */
		try {
			const updated = await updateProfile({
				firstName: firstName || undefined,
				lastName: lastName || undefined,
				about: about || undefined,
				company: company || undefined,
				website: website || undefined,
				location: location || undefined,
				themeMode,
				languageSettingsMode,
				preferredAuthMethod: preferredAuthMethod as 'email' | 'password' | 'passkey',
				newsletterOptOut
			});
			currentUser.set(updated);
			populateForm(updated);
			toastSuccess(t2('Profile updated successfully.'));

			/* Refresh the JWT so tokenData (used by AbstractIDCard/sidebar) is up to date. */
			try {
				const result = await refreshToken();
				if (result?.token) setToken(result.token);
			} catch {
				/* Non-critical — the token will refresh on its next interval. */
			}

			/* Sync theme if it changed. */
			if (themeMode === 'light' || themeMode === 'dark') {
				colorTheme.set(themeMode);
			}
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to save profile'));
		} finally {
			savingProfile = false;
		}
	}

	/**
	 * Change or set the user's password.
	 * Passwordless users only need newPassword + confirmation.
	 */
	async function handleChangePassword(): Promise<void> {
		const isPasswordless = $currentUser?.passwordless;

		/** Require current password for users who already have one. */
		if (!isPasswordless && !currentPasswordField) {
			toastWarning(t2('Please enter your current password.'));
			return;
		}
		/** Ensure a new password was provided. */
		if (!newPasswordField) {
			toastWarning(t2('Please enter a new password.'));
			return;
		}
		/** Verify new password and confirmation match. */
		if (newPasswordField !== confirmPasswordField) {
			toastWarning(t2('New passwords do not match.'));
			return;
		}
		/** Enforce minimum password length. */
		if (newPasswordField.length < 6) {
			toastWarning(t2('Password must be at least 6 characters.'));
			return;
		}

		savingPassword = true;
		/** Submit the password change and reload profile on success. */
		try {
			await changePassword(isPasswordless ? null : currentPasswordField, newPasswordField);
			currentPasswordField = '';
			newPasswordField = '';
			confirmPasswordField = '';
			toastSuccess(isPasswordless ? t2('Password created successfully.') : t2('Password changed successfully.'));
			/* Reload profile so passwordless flag clears. */
			await loadFullProfile();
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to change password'));
		} finally {
			savingPassword = false;
		}
	}

	/**
	 * Request an OTP for email change — sent to the new address.
	 */
	async function handleRequestEmailChange(): Promise<void> {
		if (!newEmailField || !newEmailField.includes('@')) {
			toastWarning(t2('Please enter a valid email address.'));
			return;
		}

		emailChangeBusy = true;
		/** Send the OTP to the new email address. */
		try {
			await requestEmailChangeOTP(newEmailField);
			emailChangeOTPSent = true;
			toastSuccess(t2('Verification code sent to {{email}}', { email: newEmailField }));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to send verification code'));
		} finally {
			emailChangeBusy = false;
		}
	}

	/**
	 * Confirm email change with the 6-digit OTP.
	 */
	async function handleConfirmEmailChange(): Promise<void> {
		if (!emailChangeCode || emailChangeCode.length !== 6) {
			toastWarning(t2('Please enter the 6-digit verification code.'));
			return;
		}

		emailChangeBusy = true;
		/** Verify the OTP and apply the email change. */
		try {
			await confirmEmailChange(emailChangeCode);
			toastSuccess(t2('Email address updated successfully.'));
			/* Reset state and reload profile. */
			newEmailField = '';
			emailChangeCode = '';
			emailChangeOTPSent = false;
			await loadFullProfile();
			/* Refresh JWT so the new email is reflected everywhere. */
			try {
				const result = await refreshToken();
				if (result?.token) setToken(result.token);
			} catch {
				/* Non-critical. */
			}
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Invalid or expired code'));
		} finally {
			emailChangeBusy = false;
		}
	}

	/**
	 * Cancel the email change flow and reset state.
	 */
	function cancelEmailChange(): void {
		emailChangeOTPSent = false;
		emailChangeCode = '';
		newEmailField = '';
	}

	/**
	 * Signs the user out.
	 *
	 * Hands off to `/logout` rather than calling `logout()` here: that route
	 * revokes the JWT server-side first, so the session is actually gone rather
	 * than merely forgotten by this browser. A full page load because `/logout`
	 * lives in the public app, not the admin one.
	 */
	function handleSignOut(): void {
		window.location.href = '/logout';
	}

	/**
	 * Open the account-deletion modal and reset its state.
	 */
	function openDeleteAccount(): void {
		deletionOTPSent = false;
		deletionCode = '';
		deletionConfirmText = '';
		deleteModalOpen = true;
	}

	/**
	 * Request the deletion OTP — sends a code to the user's current email.
	 */
	async function handleRequestAccountDeletion(): Promise<void> {
		deletionBusy = true;
		/** Send the OTP to the user's current email. */
		try {
			await requestAccountDeletionOTP();
			deletionOTPSent = true;
			toastSuccess(t2('Verification code sent to {{email}}', { email: $currentUser?.email || '' }));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to send verification code'));
		} finally {
			deletionBusy = false;
		}
	}

	/**
	 * Confirm account deletion with the OTP, then log the user out.
	 */
	async function handleConfirmAccountDeletion(): Promise<void> {
		if (!deletionCode || deletionCode.length !== 6) {
			toastWarning(t2('Please enter the 6-digit verification code.'));
			return;
		}

		deletionBusy = true;
		/** Confirm deletion server-side, then clear the session. */
		try {
			await confirmAccountDeletion(deletionCode);
			deleteModalOpen = false;
			toastSuccess(t2('Your account has been deleted.'));
			/* Clear the token and navigate to /login via full page load. */
			logout();
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Invalid or expired code'));
		} finally {
			deletionBusy = false;
		}
	}

	/** Client-side username format regex: letters, digits, underscores, 3–30 chars. */
	const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,30}$/;

	/**
	 * Debounced live check of username validity and availability.
	 * Fires 400ms after the user stops typing.
	 */
	function onUsernameInput(): void {
		/* Clear any pending timer. */
		if (usernameCheckTimer) clearTimeout(usernameCheckTimer);
		usernameCheckResult = null;
		usernameCheckMessage = '';

		const value = newUsernameField.trim();

		/* Empty — reset state. */
		if (!value) {
			usernameChecking = false;
			return;
		}

		/* Instant client-side format check. */
		if (!USERNAME_REGEX.test(value)) {
			usernameCheckResult = false;
			usernameCheckMessage = t2('Must be 3–30 characters (letters, numbers, underscores).');
			usernameChecking = false;
			return;
		}

		/* Debounce the server availability check. */
		usernameChecking = true;
		usernameCheckTimer = setTimeout(async () => {
			/** Check the server for availability. */
			try {
				const result = await checkUsernameAvailability(value);
				/* Only update if the input hasn't changed while we waited. */
				if (newUsernameField.trim() === value) {
					usernameCheckResult = result.valid && result.available;
					usernameCheckMessage = result.message;
				}
			} catch {
				usernameCheckResult = null;
				usernameCheckMessage = '';
			} finally {
				/* Only clear spinner if the input hasn't changed. */
				if (newUsernameField.trim() === value) {
					usernameChecking = false;
				}
			}
		}, 400);
	}

	/**
	 * Request an OTP for username change — sent to the user's current email.
	 */
	async function handleRequestUsernameChange(): Promise<void> {
		/* Only proceed if live check passed. */
		if (!newUsernameField || usernameCheckResult !== true) {
			toastWarning(t2('Please enter a valid, available username.'));
			return;
		}

		usernameChangeBusy = true;
		/** Send the OTP to the user's current email address. */
		try {
			await requestUsernameChangeOTP(newUsernameField);
			usernameChangeOTPSent = true;
			toastSuccess(t2('Verification code sent to your email.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to send verification code'));
		} finally {
			usernameChangeBusy = false;
		}
	}

	/**
	 * Confirm username change with the 6-digit OTP.
	 */
	async function handleConfirmUsernameChange(): Promise<void> {
		/* Validate that a full 6-digit code has been entered. */
		if (!usernameChangeCode || usernameChangeCode.length !== 6) {
			toastWarning(t2('Please enter the 6-digit verification code.'));
			return;
		}

		usernameChangeBusy = true;
		/** Verify the OTP and apply the username change. */
		try {
			await confirmUsernameChange(usernameChangeCode);
			toastSuccess(t2('Username updated successfully.'));
			/* Reset state and reload profile. */
			newUsernameField = '';
			usernameChangeCode = '';
			usernameChangeOTPSent = false;
			usernameCheckResult = null;
			usernameCheckMessage = '';
			await loadFullProfile();
			/* Refresh JWT so the new username is reflected everywhere. */
			try {
				const result = await refreshToken();
				if (result?.token) setToken(result.token);
			} catch {
				/* Non-critical. */
			}
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Invalid or expired code'));
		} finally {
			usernameChangeBusy = false;
		}
	}

	/**
	 * Cancel the username change flow and reset state.
	 */
	function cancelUsernameChange(): void {
		usernameChangeOTPSent = false;
		usernameChangeCode = '';
		newUsernameField = '';
		usernameCheckResult = null;
		usernameCheckMessage = '';
	}

	/**
	 * Triggered when the user selects a file for avatar upload.
	 */
	function handleFileSelect(e: Event): void {
		const input = e.target as HTMLInputElement;
		if (input.files && input.files[0]) {
			cropFile = input.files[0];
			showCropModal = true;
		}
		/* Reset so the same file can be re-selected. */
		input.value = '';
	}

	/**
	 * Called when the ImageCropper emits a cropped blob.
	 */
	async function handleCropped(blob: Blob): Promise<void> {
		showCropModal = false;
		cropFile = null;
		avatarBusy = true;
		avatarError = '';

		/** Upload the cropped image and refresh the JWT with the new avatar URL. */
		try {
			const updated = await uploadAvatar(blob);
			currentUser.set(updated);
			avatarCacheBust = Date.now();

			/* Refresh the JWT so the AbstractIDCard picks up the new
			   profilePictureURL embedded in the token. */
			const result = await refreshToken();
			if (result?.token) setToken(result.token);

			toastSuccess(t2('Avatar uploaded.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to upload avatar'));
		} finally {
			avatarBusy = false;
		}
	}

	/**
	 * Remove the user's avatar.
	 */
	async function handleDeleteAvatar(): Promise<void> {
		avatarBusy = true;
		avatarError = '';

		/** Delete the avatar on the server and refresh the JWT to clear the old URL. */
		try {
			const updated = await deleteAvatar();
			currentUser.set(updated);
			avatarCacheBust = Date.now();

			/* Refresh the JWT so the AbstractIDCard clears the old
			   profilePictureURL embedded in the token. */
			const result = await refreshToken();
			if (result?.token) setToken(result.token);

			toastSuccess(t2('Avatar removed.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to delete avatar'));
		} finally {
			avatarBusy = false;
		}
	}

	// ── 2FA Handlers ─────────────────────────────────────────────

	/**
	 * Begin 2FA setup — requests secret + URI, then generates QR code.
	 */
	async function handleSetup2FA(): Promise<void> {
		twoFactorBusy = true;
		twoFactorError = '';
		twoFactorSetup = null;
		twoFactorQRDataURL = '';
		backupCodes = [];

		/** Request the TOTP secret and generate a scannable QR code. */
		try {
			twoFactorSetup = await setupTwoFactor();
			/* Generate a QR code data URL from the otpauth URI. */
			twoFactorQRDataURL = await QRCode.toDataURL(twoFactorSetup.uri, { width: 256, margin: 2 });
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to start 2FA setup'));
		} finally {
			twoFactorBusy = false;
		}
	}

	/**
	 * Confirm 2FA setup by verifying the user's TOTP code.
	 * On success the server returns backup codes which we display.
	 *
	 * @param code - 6-digit TOTP code from the OTPInput component.
	 */
	async function handleConfirm2FA(code: string): Promise<void> {
		twoFactorBusy = true;
		twoFactorError = '';

		/** Verify the TOTP code with the server and store the returned backup codes. */
		try {
			const result = await confirmTwoFactor(code);
			backupCodes = result.backupCodes;
			toastSuccess(t2('Two-factor authentication enabled successfully!'));
			twoFactorSetup = null;
			twoFactorQRDataURL = '';
			/* Re-fetch the profile to reflect twoFactorEnabled = true */
			const user = await loadFullProfile();
			if (user) populateForm(user);
		} catch (err) {
			twoFactorError = err instanceof Error ? err.message : t2('Invalid code. Please try again.');
		} finally {
			twoFactorBusy = false;
		}
	}

	/**
	 * Disable 2FA using a valid TOTP or backup code.
	 */
	async function handleDisable2FA(): Promise<void> {
		if (!disable2FACode || disable2FACode.length < 6) {
			twoFactorError = t2('Please enter a valid code to disable 2FA.');
			return;
		}

		twoFactorBusy = true;
		twoFactorError = '';

		/** Send the disable request and reload the profile on success. */
		try {
			await disableTwoFactor(disable2FACode);
			toastSuccess(t2('Two-factor authentication has been disabled.'));
			showDisable2FA = false;
			disable2FACode = '';
			const user = await loadFullProfile();
			if (user) populateForm(user);
		} catch (err) {
			twoFactorError = err instanceof Error ? err.message : t2('Failed to disable 2FA');
		} finally {
			twoFactorBusy = false;
		}
	}

	/** Theme options for the select dropdown. */
	const themeOptions = [
		{ value: 'dark', name: t2('Dark'), icon: 'fa-solid fa-moon' },
		{ value: 'light', name: t2('Light'), icon: 'fa-solid fa-sun' }
	];

	/** Preferred sign-in method options for the dropdown. */
	const authMethodOptions = [
		{ value: 'email', name: t2('Email code (OTP)'), icon: 'fa-solid fa-envelope' },
		{ value: 'password', name: t2('Password'), icon: 'fa-solid fa-lock' },
		{ value: 'passkey', name: t2('Passkey'), icon: 'fa-solid fa-fingerprint' }
	];

	/** Language options derived from available locales. */
	const languageOptions = locales.map((loc) => ({
		value: loc,
		name: localeNames[loc] ?? loc,
		icon: 'fa-solid fa-language'
	}));
</script>

<Heading tag="h3">{$t("Account")}</Heading>

{#if loading}
	<div class="flex items-center justify-center py-12">
		<Spinner size="8" />
	</div>
{:else}
	<div class="mt-4 space-y-6">
		{#if error}
			<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
			</div>
		{/if}

		<!-- ─── Sign Out ─────────────────────────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-right-from-bracket me-2"></i>{$t("Sign Out")}
			</h4>
			<!-- Two whole sentences rather than a sentence broken around a link: a
			     fragment before and after an anchor cannot be reordered, and word
			     order is exactly what translation changes. -->
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{$t("Sign out on this device. Your other devices stay signed in.")}
			</p>
			<p class="mb-4 text-sm">
				<RoutedLink to="/Settings/Account/Sessions">
					<span class="text-primary-600 dark:text-primary-400 hover:underline">
						{$t("Review your active sessions to sign out elsewhere.")}
					</span>
				</RoutedLink>
			</p>
			<div class="flex justify-end">
				<Button color="alternative" onclick={handleSignOut}>
					<i class="fa-solid fa-right-from-bracket me-2"></i>{$t("Sign Out")}
				</Button>
			</div>
		</AbstractCard>

		<!-- ─── Avatar ──────────────────────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-camera me-2"></i>{$t("Profile Picture")}
			</h4>

			{#if avatarError}
				<div class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
					<i class="fa-solid fa-exclamation-triangle me-2"></i>{avatarError}
				</div>
			{/if}

			<div class="flex items-center gap-6">
				<div class="relative shrink-0">
					{#if avatarBusy}
						<div
							class="flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700"
						>
							<Spinner size="6" />
						</div>
					{:else}
						<Avatar
							src={avatarUrl || ''}
							size="xl"
							class="rounded-full ring-2 ring-gray-200 dark:ring-gray-700"
						/>
					{/if}
				</div>

				<div class="space-y-2">
					<p class="text-sm text-gray-500 dark:text-gray-400">
						{$t("Upload a square image. It will be resized to 256×256 pixels.")}
					</p>
					<div class="flex gap-2">
						<Button size="sm" onclick={() => document.getElementById('avatar-input')?.click()}>
							<i class="fa-solid fa-upload me-2"></i>{$t("Upload")}
						</Button>
						{#if $currentUser?.imageName}
							<ConfirmButton
								size="sm"
								color="alternative"
								action={$t("remove your avatar")}
								disabled={avatarBusy}
								onconfirm={handleDeleteAvatar}
							>
								<i class="fa-solid fa-trash me-2"></i>{$t("Remove")}
							</ConfirmButton>
						{/if}
					</div>
					<input
						id="avatar-input"
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						class="hidden"
						onchange={handleFileSelect}
					/>
				</div>
			</div>
		</AbstractCard>

		<!-- ─── Personal Information ────────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-user me-2"></i>{$t("Personal Information")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="firstName" class="mb-2">{$t("First Name")}</Label>
					<Input id="firstName" bind:value={firstName} placeholder={$t("First name")} />
				</div>
				<div>
					<Label for="lastName" class="mb-2">{$t("Last Name")}</Label>
					<Input id="lastName" bind:value={lastName} placeholder={$t("Last name")} />
				</div>
				<div>
					<Label for="username" class="mb-2">{$t("Username")}</Label>
					{#if usernameChangeOTPSent}
						<p class="mb-2 text-sm text-gray-600 dark:text-gray-400">
							{$t("Enter the 6-digit code sent to")} <strong>{$currentUser?.email}</strong>
						</p>
						<OTPInput
							oncomplete={(code) => {
								usernameChangeCode = code;
							}}
						/>
						<div class="mt-3 flex items-center gap-2">
							<Button
								size="xs"
								color="blue"
								onclick={handleConfirmUsernameChange}
								disabled={usernameChangeBusy || usernameChangeCode.length !== 6}
							>
								{#if usernameChangeBusy}
									<i class="fa-solid fa-spinner fa-spin me-1"></i>
								{/if}
								{$t("Confirm")}
							</Button>
							<Button size="xs" color="alternative" onclick={cancelUsernameChange}>{$t("Cancel")}</Button>
						</div>
					{:else}
						<div class="flex items-stretch gap-2">
							<div class="flex-1">
								<Input
									id="username"
									bind:value={newUsernameField}
									placeholder={$currentUser?.username || t2('New username')}
									oninput={onUsernameInput}
									color={usernameCheckResult === true ? 'green' : usernameCheckResult === false ? 'red' : undefined}
								>
									{#snippet left()}
										<i class="fa-solid fa-at text-gray-400"></i>
									{/snippet}
									{#snippet right()}
										{#if usernameChecking}
											<i class="fa-solid fa-spinner fa-spin text-gray-400"></i>
										{:else if usernameCheckResult === true}
											<i class="fa-solid fa-check text-green-500"></i>
										{:else if usernameCheckResult === false}
											<i class="fa-solid fa-xmark text-red-500"></i>
										{/if}
									{/snippet}
								</Input>
							</div>
							<Button
								color="blue"
								onclick={handleRequestUsernameChange}
								disabled={usernameChangeBusy || usernameCheckResult !== true}
							>
								{#if usernameChangeBusy}
									<i class="fa-solid fa-spinner fa-spin"></i>
								{:else}
									<i class="fa-solid fa-pen"></i>
								{/if}
							</Button>
						</div>
						{#if usernameCheckMessage}
							<Helper class="mt-1" color={usernameCheckResult === true ? 'green' : usernameCheckResult === false ? 'red' : 'gray'}>
								{usernameCheckMessage}
							</Helper>
						{:else}
							<Helper class="mt-1">{$t("Current:")}&nbsp;@{$currentUser?.username || ''}</Helper>
						{/if}
					{/if}
				</div>
				<div>
					<Label for="email" class="mb-2">{$t("Email")}</Label>
					{#if emailChangeOTPSent}
						<p class="mb-2 text-sm text-gray-600 dark:text-gray-400">
							{$t("Enter the 6-digit code sent to")} <strong>{newEmailField}</strong>
						</p>
						<OTPInput
							oncomplete={(code) => {
								emailChangeCode = code;
							}}
						/>
						<div class="mt-3 flex items-center gap-2">
							<Button
								size="xs"
								color="blue"
								onclick={handleConfirmEmailChange}
								disabled={emailChangeBusy || emailChangeCode.length !== 6}
							>
								{#if emailChangeBusy}
									<i class="fa-solid fa-spinner fa-spin me-1"></i>
								{/if}
								{$t("Confirm")}
							</Button>
							<Button size="xs" color="alternative" onclick={cancelEmailChange}>{$t("Cancel")}</Button>
						</div>
					{:else}
						<div class="flex items-stretch gap-2">
							<div class="flex-1">
								<Input
									id="email"
									bind:value={newEmailField}
									placeholder={$currentUser?.email || t2('New email address')}
								>
									{#snippet left()}
										<i class="fa-solid fa-envelope text-gray-400"></i>
									{/snippet}
								</Input>
							</div>
							<Button
								color="blue"
								onclick={handleRequestEmailChange}
								disabled={emailChangeBusy || !newEmailField}
							>
								{#if emailChangeBusy}
									<i class="fa-solid fa-spinner fa-spin"></i>
								{:else}
									<i class="fa-solid fa-pen"></i>
								{/if}
							</Button>
						</div>

						<Helper class="mt-1">{$t("Current:")}&nbsp;{$currentUser?.email || ''}</Helper>
					{/if}
				</div>
			</div>

			<div class="mt-4">
				<Label for="about" class="mb-2">{$t("About")}</Label>
				<Textarea id="about" bind:value={about} rows={3} placeholder={$t("A short bio or description...")} class="w-full" />
			</div>
		</AbstractCard>

		<!-- ─── Contact ─────────────────────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-address-card me-2"></i>{$t("Contact Details")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="company" class="mb-2">{$t("Company")}</Label>
					<Input id="company" bind:value={company} placeholder={$t("Company name")}>
						{#snippet left()}
												<i  class="fa-solid fa-building text-gray-400"></i>
											{/snippet}
					</Input>
				</div>
				<div>
					<Label for="location" class="mb-2">{$t("Location")}</Label>
					<Input id="location" bind:value={location} placeholder={$t("City, Country")}>
						{#snippet left()}
												<i  class="fa-solid fa-location-dot text-gray-400"></i>
											{/snippet}
					</Input>
				</div>
				<div class="md:col-span-2">
					<Label for="website" class="mb-2">{$t("Website")}</Label>
					<Input id="website" bind:value={website} placeholder="https://">
						{#snippet left()}
												<i  class="fa-solid fa-globe text-gray-400"></i>
											{/snippet}
					</Input>
				</div>
			</div>
		</AbstractCard>

		<!-- ─── Preferences ─────────────────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-sliders me-2"></i>{$t("Preferences")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="themeMode" class="mb-2">{$t("Theme")}</Label>
					<AbstractDropdown items={themeOptions} bind:value={themeMode} class="w-full" />
				</div>
				<div>
					<Label for="languageMode" class="mb-2">{$t("Language")}</Label>
					<AbstractDropdown items={languageOptions} bind:value={languageSettingsMode} onchange={(v) => locale.set(String(v))} class="w-full" />
				</div>
			</div>

			<div class="mt-4">
				<Label for="preferredAuthMethod" class="mb-2">{$t("Preferred sign-in method")}</Label>
				<AbstractDropdown items={authMethodOptions} bind:value={preferredAuthMethod} class="w-full" />
				<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
					{$t("After you enter your email on the login page, this method is offered first. Password and passkey require that you have set one up.")}
				</p>
			</div>

			<div class="mt-4">
				<Toggle bind:checked={newsletterOptOut}>{$t("Opt out of newsletters")}</Toggle>
				<p class="ml-11 mt-1 text-sm text-gray-500 dark:text-gray-400">
					{$t("When enabled, you won't receive promotional emails.")}
				</p>
			</div>
		</AbstractCard>

		<!-- Save Profile button -->
		<div class="flex justify-end">
			<Button onclick={handleSaveProfile} disabled={savingProfile}>
				{#if savingProfile}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Saving...")}
				{:else}
					<i class="fa-solid fa-save me-2"></i>{$t("Save Profile")}
				{/if}
			</Button>
		</div>

		<!-- ─── Two-Factor Authentication ─────────────────────────── -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-shield-halved me-2"></i>{$t("Two-Factor Authentication")}
			</h4>

			{#if twoFactorError}
				<div class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
					<i class="fa-solid fa-exclamation-triangle me-2"></i>{twoFactorError}
				</div>
			{/if}

			{#if $currentUser?.twoFactorEnabled}
				<!-- 2FA is currently enabled -->
				<div class="mb-4 flex items-center gap-3">
					<div class="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
						<i class="fa-solid fa-check-circle text-green-600 dark:text-green-400"></i>
					</div>
					<div>
						<p class="font-medium text-gray-900 dark:text-white">{$t("2FA is enabled")}</p>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							{$t("Your account is protected with TOTP authenticator.")}
						</p>
					</div>
				</div>

				{#if !showDisable2FA}
					<Button color="red" size="sm" onclick={() => (showDisable2FA = true)}>
						<i class="fa-solid fa-shield me-2"></i>{$t("Disable 2FA")}
					</Button>
				{:else}
					<div class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/10">
						<p class="mb-3 text-sm text-red-700 dark:text-red-300">
							{$t("Enter a TOTP code or backup code to disable two-factor authentication.")}
						</p>
						<div class="flex items-center gap-3">
							<Input
								bind:value={disable2FACode}
								placeholder={$t("6-digit code")}
								maxlength={8}
								class="max-w-[200px]"
							/>
							<Button color="red" size="sm" onclick={handleDisable2FA} disabled={twoFactorBusy}>
								{#if twoFactorBusy}
									<i class="fa-solid fa-spinner fa-spin me-2"></i>
								{/if}
								{$t("Confirm Disable")}
							</Button>
							<Button
								color="alternative"
								size="sm"
								onclick={() => {
									showDisable2FA = false;
									disable2FACode = '';
								}}
							>
								{$t("Cancel")}
							</Button>
						</div>
					</div>
				{/if}
			{:else if twoFactorSetup}
				<!-- 2FA setup flow: show QR code + confirmation input -->
				<div class="space-y-4">
					<!-- QR Code -->
					<div class="text-center">
						<p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
							{$t("Scan with your authenticator app:")}
						</p>
						{#if twoFactorQRDataURL}
							<img
								src={twoFactorQRDataURL}
								alt={$t("2FA QR Code")}
								class="mx-auto h-48 w-48 rounded-lg border border-gray-200 dark:border-gray-700"
							/>
						{/if}
						<p class="mt-2 text-xs text-gray-400 dark:text-gray-500">{$t("Or enter manually:")}</p>
						<code class="select-all break-all rounded bg-depth px-2 py-1 text-xs">
							{twoFactorSetup.secret}
						</code>
					</div>

					<!-- Confirm with TOTP code -->
					<div class="bg-depth rounded-lg border border-gray-200 p-4 dark:border-gray-700">
						<p class="mb-3 text-center text-sm text-gray-700 dark:text-gray-300">
							{$t("Enter the 6-digit code from your authenticator to complete setup:")}
						</p>
						<OTPInput oncomplete={handleConfirm2FA} error={twoFactorError ? ' ' : ''} />
						<div class="flex items-center justify-center gap-3">
							{#if twoFactorBusy}
								<Spinner size="5" />
							{/if}
							<Button
								color="alternative"
								size="xs"
								onclick={() => {
									twoFactorSetup = null;
								}}
							>
								{$t("Cancel")}
							</Button>
						</div>
					</div>
				</div>
			{:else if backupCodes.length > 0}
				<!-- Backup codes shown after successful 2FA confirmation -->
				<div class="space-y-4">
					<p class="text-sm font-medium text-gray-900 dark:text-white">
						<i class="fa-solid fa-key me-1"></i>{$t("Backup Codes")}
					</p>
					<p class="text-xs text-gray-500 dark:text-gray-400">
						{$t("Save these codes in a safe place. Each can be used once if you lose your authenticator. They will not be shown again.")}
					</p>
					<div class="grid grid-cols-2 gap-1">
						{#each backupCodes as code (code)}
							<code
								class="bg-depth select-all rounded px-2 py-1 text-center font-mono text-sm"
							>
								{code}
							</code>
						{/each}
					</div>
					<Button
						color="alternative"
						size="sm"
						onclick={() => {
							backupCodes = [];
						}}
					>
						{$t("I've saved my codes")}
					</Button>
				</div>
			{:else}
				<!-- 2FA not enabled — offer setup -->
				<div class="mb-4 flex items-center gap-3">
					<div class="bg-depth rounded-lg p-2">
						<i class="fa-solid fa-shield text-gray-400 dark:text-gray-500"></i>
					</div>
					<div>
						<p class="font-medium text-gray-900 dark:text-white">{$t("2FA is not enabled")}</p>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							{$t("Add an extra layer of security using a TOTP authenticator app.")}
						</p>
					</div>
				</div>
				<Button onclick={handleSetup2FA} disabled={twoFactorBusy}>
					{#if twoFactorBusy}
						<i class="fa-solid fa-spinner fa-spin me-2"></i>
					{:else}
						<i class="fa-solid fa-shield-halved me-2"></i>
					{/if}
					{$t("Enable 2FA")}
				</Button>
			{/if}
		</AbstractCard>

		<!-- ─── Change Password ─────────────────────────────────── -->
		<AbstractCard class="max-w-none">
			{#if $currentUser?.passwordless}
				<h4 class="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-shield-halved me-2"></i>{$t("Create a Password")}
				</h4>
				<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
					{$t("Increase security by creating a password for your account.")}
				</p>

				<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div>
						<Label for="newPassword" class="mb-2">{$t("New Password")}</Label>
						<Input id="newPassword" type="password" bind:value={newPasswordField} placeholder="••••••••" />
					</div>
					<div>
						<Label for="confirmPassword" class="mb-2">{$t("Confirm Password")}</Label>
						<Input
							id="confirmPassword"
							type="password"
							bind:value={confirmPasswordField}
							placeholder="••••••••"
						/>
					</div>
				</div>

				<div class="mt-4 flex justify-end">
					<Button color="blue" onclick={handleChangePassword} disabled={savingPassword}>
						{#if savingPassword}
							<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Creating...")}
						{:else}
							<i class="fa-solid fa-lock me-2"></i>{$t("Create Password")}
						{/if}
					</Button>
				</div>
			{:else}
				<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-lock me-2"></i>{$t("Change Password")}
				</h4>

				<div class="grid grid-cols-1 gap-4 md:grid-cols-3">
					<div>
						<Label for="currentPassword" class="mb-2">{$t("Current Password")}</Label>
						<Input
							id="currentPassword"
							type="password"
							bind:value={currentPasswordField}
							placeholder="••••••••"
						/>
					</div>
					<div>
						<Label for="newPassword" class="mb-2">{$t("New Password")}</Label>
						<Input id="newPassword" type="password" bind:value={newPasswordField} placeholder="••••••••" />
					</div>
					<div>
						<Label for="confirmPassword" class="mb-2">{$t("Confirm Password")}</Label>
						<Input
							id="confirmPassword"
							type="password"
							bind:value={confirmPasswordField}
							placeholder="••••••••"
						/>
					</div>
				</div>

				<div class="mt-4 flex justify-end">
					<Button color="alternative" onclick={handleChangePassword} disabled={savingPassword}>
						{#if savingPassword}
							<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Changing...")}
						{:else}
							<i class="fa-solid fa-key me-2"></i>{$t("Change Password")}
						{/if}
					</Button>
				</div>
			{/if}
		</AbstractCard>

		<!-- ─── Danger Zone — Account Deletion (GDPR) ───────────────── -->
		<AbstractCard class="max-w-none border border-red-300 dark:border-red-800">
			<h4 class="mb-4 text-lg font-semibold text-red-700 dark:text-red-400">
				<i class="fa-solid fa-triangle-exclamation me-2"></i>{$t("Delete Account")}
			</h4>
			<p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
				{$t("Permanently delete your account. You will be signed out and will no longer be able to access your data. This action requires email verification.")}
			</p>
			<div class="flex justify-end">
				<Button color="red" onclick={openDeleteAccount}>
					<i class="fa-solid fa-trash me-2"></i>{$t("Delete Account")}
				</Button>
			</div>
		</AbstractCard>
	</div>
{/if}

<!-- ─── Delete Account Modal ───────────────────────────────────── -->
<Modal bind:open={deleteModalOpen} title={$t("Delete Account")} size="sm" autoclose={false}>
	<div class="text-center">
		<i class="fa-solid fa-triangle-exclamation mx-auto mb-4 text-5xl text-red-500"></i>
		{#if deletionOTPSent}
			<p class="mb-2 text-sm text-gray-600 dark:text-gray-400">
				{$t("Enter the 6-digit code sent to")} <strong>{$currentUser?.email || ''}</strong>
			</p>
			<div class="flex justify-center">
				<OTPInput
					oncomplete={(code) => {
						deletionCode = code;
					}}
				/>
			</div>
			<div class="mt-4">
				<Label for="deleteConfirm" class="mb-2 text-left">
					{$t("Type DELETE to confirm")}
				</Label>
				<Input id="deleteConfirm" bind:value={deletionConfirmText} placeholder="DELETE" />
			</div>
			<div class="mt-4 flex justify-center gap-2">
				<Button color="alternative" onclick={() => (deleteModalOpen = false)}>{$t("Cancel")}</Button>
				<Button
					color="red"
					onclick={handleConfirmAccountDeletion}
					disabled={deletionBusy || deletionCode.length !== 6 || deletionConfirmText !== 'DELETE'}
				>
					{#if deletionBusy}
						<i class="fa-solid fa-spinner fa-spin me-2"></i>
					{/if}
					{$t("Delete My Account")}
				</Button>
			</div>
		{:else}
			<p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
				{$t("This will permanently delete your account. We'll send a verification code to your email to confirm. This cannot be undone.")}
			</p>
			<div class="flex justify-center gap-2">
				<Button color="alternative" onclick={() => (deleteModalOpen = false)}>{$t("Cancel")}</Button>
				<Button color="red" onclick={handleRequestAccountDeletion} disabled={deletionBusy}>
					{#if deletionBusy}
						<i class="fa-solid fa-spinner fa-spin me-2"></i>
					{/if}
					{$t("Send Verification Code")}
				</Button>
			</div>
		{/if}
	</div>
</Modal>

<!-- ─── Image Crop Modal ───────────────────────────────────────── -->
<Modal bind:open={showCropModal} title={$t("Crop Profile Picture")} size="lg" autoclose={false}>
	{#if cropFile}
		<ImageCropper
			sourceFile={cropFile}
			oncrop={handleCropped}
			oncancel={() => {
				showCropModal = false;
				cropFile = null;
			}}
		/>
	{/if}
</Modal>
